<script setup lang="ts">
import { nextTick, onMounted, provide, ref, watch } from 'vue';
import type { Ref } from 'vue';
import DncFieldMapper from '../DncFieldMapper/DncFieldMapper.vue';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DtToast } from '@dialpad/dialtone/vue3/lib/toast';
import { DtStack } from '@dialpad/dialtone/vue3/lib/stack';
import { DATA_QA } from './dnc-import-map-step.constants';
import { usePhoneCsvStore } from '@/stores';
import type { PhoneCsv } from '@/apis/generated/dialbunny';
import logger from '@/logger';
import { DncFieldMapperUtil } from '@/dnc/utils/dnc-field-mapper-util';
import type {
  DncFieldMap,
  DncCustomFieldMap,
} from '@/dnc/utils/dnc-field-mapping-enums';
import {
  getStoredDncMap,
  getStoredDncCustomMap,
  storeDncMap,
  storeDncCustomMap,
} from '@/csv/utils/csv-map-storage';

const log = logger.getChild('DncImportMapStep');

log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

const usesStoredMap = ref(false);
const errors = ref(new Map<string, string | null>());
const loading = ref(false);

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);

const setError = (key: string, error: string | null) => {
  log.debug('Setting error', key, error);
  errors.value.set(key, error);
};

const dncFieldMapperUtil = new DncFieldMapperUtil();
const phoneCsvStore = usePhoneCsvStore();

const dncCsv = defineModel<PhoneCsv | null>({
  required: true,
});

const props = defineProps<{
  examples: string[];
}>();

const emit = defineEmits(['continue', 'back']);

const checkBackendMappings = (csv: PhoneCsv) => {
  const hasMapFromBackend = Object.keys(csv.mapping ?? {}).length > 0;
  const hasCustomMapFromBackend = false;

  return { hasMapFromBackend, hasCustomMapFromBackend };
};

const getStoredMappings = async (
  csv: PhoneCsv,
  hasMapFromBackend: boolean,
  hasCustomMapFromBackend: boolean,
) => {
  const companyIdStr = csv.companyId ? csv.companyId.toString() : '';

  const storedMap = hasMapFromBackend
    ? csv.mapping ?? {}
    : companyIdStr
    ? await getStoredDncMap(companyIdStr, csv.columns ?? [])
    : {};

  const storedCustomMap = hasCustomMapFromBackend
    ? {}
    : companyIdStr
    ? await getStoredDncCustomMap(companyIdStr, csv.columns ?? [])
    : {};

  return { storedMap, storedCustomMap };
};

const handleDncCsvChange = async (csv: PhoneCsv) => {
  log.debug('DNC CSV changed', csv);

  const { hasMapFromBackend, hasCustomMapFromBackend } =
    checkBackendMappings(csv);
  const { storedMap, storedCustomMap } = await getStoredMappings(
    csv,
    hasMapFromBackend,
    hasCustomMapFromBackend,
  );

  log.debug(`hasMapFromBackend: ${hasMapFromBackend}, stored map`, storedMap);

  usesStoredMap.value =
    Object.keys(storedMap ?? {}).length > 0 ||
    Object.keys(storedCustomMap ?? {}).length > 0;

  log.debug(
    'Initializing dncFieldMapperUtil',
    csv.columns,
    'examples:',
    props.examples,
  );

  log.debug('Stored maps:', { storedMap, storedCustomMap });

  const result = dncFieldMapperUtil.init(
    {
      map: storedMap as DncFieldMap,
      customMap: storedCustomMap as DncCustomFieldMap,
    },
    csv.columns ?? [],
    props.examples,
  );

  log.debug('Field mapper init result:', result);
  log.debug('Field mapper fields after init:', dncFieldMapperUtil.fields.value);
};

watch(
  dncCsv,
  async () => {
    if (!dncCsv.value) return;
    await handleDncCsvChange(dncCsv.value);
  },
  { immediate: true },
);

const validate = async (): Promise<boolean> => {
  log.debug('Validating..');
  validationTrigger.value++;

  await nextTick();

  const isValid = [...errors.value.values()].every((error) => error === null);

  log.debug('Validation result:', isValid);
  return isValid;
};

const handlePatchError = async (error: unknown) => {
  log.error('Patch error:', error);
};

const submit = async () => {
  log.debug('Submitting..');
  if (!(await validate())) {
    return;
  }
  const id = dncCsv.value?.id;
  if (!id) {
    throw new Error('DNC CSV id is not set');
  }

  if (!dncFieldMapperUtil.validate()) {
    log.debug('dncFieldMapperUtil validation failed');
    return;
  }

  const mapping = dncFieldMapperUtil.map as Ref<Record<string, string>>;
  const customMapping = dncFieldMapperUtil.customMap as Ref<
    Record<string, string>
  >;

  loading.value = true;
  log.debug('Updating phone CSV record...');

  phoneCsvStore.update(
    id,
    {
      mapping: { ...mapping.value, ...customMapping.value },
    },
    {
      onSuccess: async (updated) => {
        dncCsv.value = updated;
        log.debug('Update successful');

        if (dncCsv.value.columns && dncCsv.value.companyId) {
          log.debug('Storing DNC map');
          const companyIdStr = dncCsv.value.companyId.toString();
          await storeDncMap(companyIdStr, dncCsv.value.columns, mapping.value);
          await storeDncCustomMap(
            companyIdStr,
            dncCsv.value.columns,
            customMapping.value,
          );
          log.debug('DNC map stored');
        }

        await phoneCsvStore.refresh();

        emit('continue');
        loading.value = false;
      },
      onRetry: () => {},
      onError: async (error) => {
        await handlePatchError(error);
        loading.value = false;
      },
    },
  );
};
</script>
<template>
  <div :data-qa="DATA_QA.ROOT" class="dnc-import-map-step">
    <p class="d-fc-tertiary">
      {{ $t('DNC_IMPORT_MAP_SUBTITLE') }}
    </p>
    <div class="d-ba d-baw1 d-bc-default d-bar8 d-my16">
      <DtToast
        :message="$t('DNC_IMPORT_FAMILIAR_FORMAT')"
        :show="usesStoredMap"
        kind="success"
        :hide-icon="true"
        :close-button-props="{
          'data-qa': DATA_QA.FAMILIAR_NOTICE_CLOSE,
          ariaLabel: $t('DNC_IMPORT_CLOSE_FAMILIAR_TOAST_ARIA_LABEL'),
        }"
        @close="usesStoredMap = false"
      />
      <DncFieldMapper
        :dnc-field-mapper-util="dncFieldMapperUtil"
        :fields="dncCsv?.columns ?? []"
        @update:error="(key, value) => setError(key, value)"
      />
    </div>
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtStack direction="row" gap="400">
        <DtButton
          :data-qa="DATA_QA.BACK"
          importance="clear"
          @click="emit('back')"
        >
          {{ $t('DNC_IMPORT_BACK') }}
        </DtButton>
        <DtButton :data-qa="DATA_QA.NEXT" :loading="loading" @click="submit">
          {{ $t('DNC_IMPORT_CONTINUE') }}
        </DtButton>
      </DtStack>
    </div>
  </div>
</template>
<style>
.dnc-import-map-step .d-toast {
  max-width: 100% !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  box-shadow: none !important;
  border-bottom: 1px solid var(--dt-color-border-default) !important;
  background-clip: padding-box !important;
  padding-top: 16px !important;
  padding-bottom: 16px !important;
}
</style>
